import React, { type FC, useContext } from 'react';
import { FormikContext, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { LabelValue } from 'components/Shared/LabelValue';
import { formatDisplayValue } from './formatDisplayValue';
import { type FormControlWrapperType, type FormControlWrapperProps } from './interfaces';

const BARE_TYPES: FormControlWrapperType[] = ['radio', 'checkbox'];

export const FormControlWrapper: FC<FormControlWrapperProps> = (props) => {
    const {
        name,
        label,
        isReadonly: displayOnly = false,
        type,
        options,
        dateFormat,
        formatDisplayValue: formatOverride,
        className,
        error,
        helperText,
        fullWidth,
        required,
        disabled,
        children,
    } = props;

    const formikContext = useContext(FormikContext);
    const { t } = useTranslation('common');

    if (!displayOnly) {
        if (type && BARE_TYPES.includes(type)) {
            return (
                <FormControl error={error} fullWidth={fullWidth} required={required} disabled={disabled} className={className}>
                    {type === 'radio' && <FormLabel>{label}</FormLabel>}
                    {children}
                    {helperText && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
            );
        }

        return React.cloneElement(children, {
            label: children.props.label ?? label,
            error: children.props.error ?? error,
            helperText: children.props.helperText ?? helperText,
            fullWidth: children.props.fullWidth ?? fullWidth,
            required: children.props.required ?? required,
            disabled: children.props.disabled ?? disabled,
            className: children.props.className ?? className,
        });
    }

    if (!formikContext) {
        throw new Error(
            'FormControlWithReadonlyField: no Formik context found. Wrap the form in <FormikProvider value={formik}> ' +
            'to use this component with forms built on useFormik().'
        );
    }

    const rawValue = getIn(formikContext.values, name);
    const displayValue = formatOverride
        ? formatOverride(rawValue)
        : formatDisplayValue(rawValue, { type, options, dateFormat, t });

    return (
        <LabelValue
            label={label}
            value={displayValue as string | number | React.ReactElement}
            className={className}
            emptyValue=""
        />
    );
};
